import React, { createContext, useContext, useReducer } from 'react';
import apiClient from '../config/axios';

// Initial state
const initialState = {
  users: [],
  courses: [],
  members: [],
  payments: [],
  membershipList: [],
  enrollments: [],
  stats: {
    totalUsers: 0,
    totalCourses: 0,
    totalMembers: 0,
    totalPayments: 0,
    totalMembershipList: 0,
  },
  loading: false,
  error: null,
};

// Action types
const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_DATA: 'SET_DATA',
  UPDATE_USERS: 'UPDATE_USERS',
  UPDATE_COURSES: 'UPDATE_COURSES',
  UPDATE_MEMBERS: 'UPDATE_MEMBERS',
  UPDATE_PAYMENTS: 'UPDATE_PAYMENTS',
  UPDATE_MEMBERSHIP_LIST: 'UPDATE_MEMBERSHIP_LIST',
  UPDATE_ENROLLMENTS: 'UPDATE_ENROLLMENTS',
  DELETE_PAYMENT: 'DELETE_PAYMENT',
  ADD_PAYMENT: 'ADD_PAYMENT',
  ADD_USER: 'ADD_USER',
  UPDATE_USER: 'UPDATE_USER',
  DELETE_USER: 'DELETE_USER',
  ADD_MEMBER: 'ADD_MEMBER',
  UPDATE_MEMBER: 'UPDATE_MEMBER',
  DELETE_MEMBER: 'DELETE_MEMBER',
  ADD_MEMBERSHIP_LIST: 'ADD_MEMBERSHIP_LIST',
  UPDATE_MEMBERSHIP_LIST_ITEM: 'UPDATE_MEMBERSHIP_LIST_ITEM',
  DELETE_MEMBERSHIP_LIST: 'DELETE_MEMBERSHIP_LIST',
  ADD_COURSE: 'ADD_COURSE',
  UPDATE_COURSE: 'UPDATE_COURSE',
  DELETE_COURSE: 'DELETE_COURSE',
  ADD_ENROLLMENT: 'ADD_ENROLLMENT',
  UPDATE_ENROLLMENT: 'UPDATE_ENROLLMENT',
  DELETE_ENROLLMENT: 'DELETE_ENROLLMENT',
};

// Reducer
const adminReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };

    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload };

    case ACTIONS.SET_DATA:
      return {
        ...state,
        users: action.payload.users || [],
        courses: action.payload.courses || [],
        members: action.payload.members || [],
        payments: action.payload.payments || [],
        membershipList: action.payload.membershipList || [],
        enrollments: action.payload.enrollments || [],
        stats: {
          totalUsers: action.payload.users?.length || 0,
          totalCourses: action.payload.courses?.length || 0,
          totalMembers: action.payload.members?.length || 0,
          totalPayments: action.payload.payments?.length || 0,
          totalMembershipList: action.payload.membershipList?.length || 0,
        },
      };

    case ACTIONS.UPDATE_USERS:
      return {
        ...state,
        users: action.payload,
        stats: { ...state.stats, totalUsers: action.payload.length },
      };

    case ACTIONS.UPDATE_COURSES:
      return {
        ...state,
        courses: action.payload,
        stats: { ...state.stats, totalCourses: action.payload.length },
      };

    case ACTIONS.UPDATE_MEMBERS:
      return {
        ...state,
        members: action.payload,
        stats: { ...state.stats, totalMembers: action.payload.length },
      };

    case ACTIONS.UPDATE_PAYMENTS:
      return {
        ...state,
        payments: action.payload,
        stats: { ...state.stats, totalPayments: action.payload.length },
      };

    case ACTIONS.UPDATE_MEMBERSHIP_LIST:
      return {
        ...state,
        membershipList: action.payload,
        stats: { ...state.stats, totalMembershipList: action.payload.length },
      };

    case ACTIONS.UPDATE_ENROLLMENTS:
      return { ...state, enrollments: action.payload };

    case ACTIONS.DELETE_PAYMENT:
      return {
        ...state,
        payments: state.payments.filter((p) => p._id !== action.payload),
        stats: { ...state.stats, totalPayments: state.stats.totalPayments - 1 },
      };

    case ACTIONS.ADD_PAYMENT:
      return {
        ...state,
        payments: [...state.payments, action.payload],
        stats: { ...state.stats, totalPayments: state.stats.totalPayments + 1 },
      };

    case ACTIONS.ADD_USER:
      return {
        ...state,
        users: [...state.users, action.payload],
        stats: { ...state.stats, totalUsers: state.stats.totalUsers + 1 },
      };

    case ACTIONS.UPDATE_USER:
      return {
        ...state,
        users: state.users.map((u) => {
          if (u._id === action.payload._id) {
            // Merge i dati esistenti con quelli aggiornati
            return { ...u, ...action.payload };
          }
          return u;
        }),
      };

    case ACTIONS.DELETE_USER:
      return {
        ...state,
        users: state.users.filter((u) => u._id !== action.payload),
        stats: { ...state.stats, totalUsers: state.stats.totalUsers - 1 },
      };

    case ACTIONS.ADD_MEMBER:
      return {
        ...state,
        members: [...state.members, action.payload],
        stats: { ...state.stats, totalMembers: state.stats.totalMembers + 1 },
      };

    case ACTIONS.UPDATE_MEMBER:
      return {
        ...state,
        members: state.members.map((m) => {
          if (m._id === action.payload._id) {
            return { ...m, ...action.payload };
          }
          return m;
        }),
      };

    case ACTIONS.DELETE_MEMBER:
      return {
        ...state,
        members: state.members.filter((m) => m._id !== action.payload),
        stats: { ...state.stats, totalMembers: state.stats.totalMembers - 1 },
      };

    case ACTIONS.ADD_MEMBERSHIP_LIST:
      return {
        ...state,
        membershipList: [...state.membershipList, action.payload],
        stats: { ...state.stats, totalMembershipList: state.stats.totalMembershipList + 1 },
      };

    case ACTIONS.UPDATE_MEMBERSHIP_LIST_ITEM:
      return {
        ...state,
        membershipList: state.membershipList.map((ml) => {
          if (ml._id === action.payload._id) {
            return { ...ml, ...action.payload };
          }
          return ml;
        }),
      };

    case ACTIONS.DELETE_MEMBERSHIP_LIST:
      return {
        ...state,
        membershipList: state.membershipList.filter((ml) => ml._id !== action.payload),
        stats: { ...state.stats, totalMembershipList: state.stats.totalMembershipList - 1 },
      };

    case ACTIONS.ADD_COURSE:
      return {
        ...state,
        courses: [...state.courses, action.payload],
        stats: { ...state.stats, totalCourses: state.stats.totalCourses + 1 },
      };

    case ACTIONS.UPDATE_COURSE:
      return {
        ...state,
        courses: state.courses.map((c) => {
          if (c._id === action.payload._id) {
            // Merge i dati esistenti con quelli aggiornati
            const updatedCourse = { ...c, ...action.payload };

            // Se teacherId è null, impostiamo anche teacher a null (forza la rimozione)
            if (updatedCourse.teacherId === null || updatedCourse.teacherId === undefined) {
              updatedCourse.teacher = null;
            }
            // Altrimenti, se abbiamo un teacherId ma non un oggetto teacher completo, popoliamo l'oggetto teacher
            else if (updatedCourse.teacherId && !updatedCourse.teacher) {
              const teacherUser = state.users.find((user) => user._id === updatedCourse.teacherId);
              if (teacherUser) {
                updatedCourse.teacher = teacherUser;
              }
            }

            return updatedCourse;
          }
          return c;
        }),
      };

    case ACTIONS.DELETE_COURSE:
      return {
        ...state,
        courses: state.courses.filter((c) => c._id !== action.payload),
        stats: { ...state.stats, totalCourses: state.stats.totalCourses - 1 },
      };

    case ACTIONS.ADD_ENROLLMENT:
      return {
        ...state,
        enrollments: [...state.enrollments, action.payload],
      };

    case ACTIONS.UPDATE_ENROLLMENT:
      return {
        ...state,
        enrollments: state.enrollments.map((e) => {
          if (e._id === action.payload._id) {
            const updated = { ...e, ...action.payload };
            if ('status' in action.payload && (action.payload.status === undefined || action.payload.status === null)) {
              delete updated.status;
            }
            if ('suspendedAt' in action.payload && (action.payload.suspendedAt === undefined || action.payload.suspendedAt === null)) {
              delete updated.suspendedAt;
            }
            return updated;
          }
          return e;
        }),
      };

    case ACTIONS.DELETE_ENROLLMENT:
      return {
        ...state,
        enrollments: state.enrollments.filter((e) => e._id !== action.payload),
      };

    default:
      return state;
  }
};

// Context
const AdminContext = createContext();

// Provider
export const AdminProvider = ({ children }) => {
  const [state, dispatch] = useReducer(adminReducer, initialState);

  // Fetch all data
  const fetchAllData = async () => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    try {
      const [usersRes, coursesRes, membersRes, paymentsRes, membershipListRes, enrollmentsRes] = await Promise.all([apiClient.get('/api/user'), apiClient.get('/api/courses'), apiClient.get('/api/memberships'), apiClient.get('/api/payments'), apiClient.get('/api/membership-list'), apiClient.get('/api/enrollments')]);

      // console.log('usersRes', usersRes.data);
      // console.log('coursesRes', coursesRes.data);
      // console.log('membersRes', membersRes.data);
      // console.log('paymentsRes', paymentsRes.data);
      // console.log('membershipListRes', membershipListRes.data.data);
      // console.log('enrollmentsRes', enrollmentsRes.data);
      dispatch({
        type: ACTIONS.SET_DATA,
        payload: {
          users: usersRes.data,
          courses: coursesRes.data,
          members: membersRes.data,
          payments: paymentsRes.data,
          membershipList: membershipListRes.data.data,
          enrollments: enrollmentsRes.data,
        },
      });
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Errore nel caricamento dei dati' });
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  };

  // CRUD operations
  const updateUser = async (userData) => {
    try {
      const response = await apiClient.patch('/api/user/update-user', userData);

      // Passa sia l'ID che i dati aggiornati al reducer
      dispatch({
        type: ACTIONS.UPDATE_USER,
        payload: { _id: userData._id, ...response.data },
      });
      return { success: true };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Errore durante la modifica utente' });
      return { success: false, error: error.message };
    }
  };

  const deleteUser = async (userId) => {
    // const normalizeId = (value) => {
    //   if (value === null || value === undefined) {
    //     return null;
    //   }
    //   if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    //     return value.toString();
    //   }
    //   if (typeof value === 'object') {
    //     if (value._id !== undefined && value._id !== null) {
    //       return normalizeId(value._id);
    //     }
    //     if (value.id !== undefined && value.id !== null) {
    //       return normalizeId(value.id);
    //     }
    //     if (typeof value.toString === 'function') {
    //       const stringified = value.toString();
    //       if (stringified && stringified !== '[object Object]') {
    //         return stringified;
    //       }
    //     }
    //   }
    //   return null;
    // };

    try {
      await apiClient.delete(`/api/auth/delete-account/${userId}`);

      // const normalizedUserId = normalizeId(userId) ?? (userId !== undefined && userId !== null ? String(userId) : null);

      dispatch({ type: ACTIONS.DELETE_USER, payload: userId });

      // console.log('state.members', state.members[0]);
      const filteredMembers = state.members.filter((member) => {
        return member.userId !== userId;
      });
      if (filteredMembers.length !== state.members.length) {
        dispatch({ type: ACTIONS.UPDATE_MEMBERS, payload: filteredMembers });
      }

      // console.log('state.enrollments', state.enrollments[0]);
      const filteredEnrollments = state.enrollments.filter((enrollment) => {
        return enrollment.userId !== userId;
      });
      if (filteredEnrollments.length !== state.enrollments.length) {
        dispatch({ type: ACTIONS.UPDATE_ENROLLMENTS, payload: filteredEnrollments });
      }

      return { success: true };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: "Errore durante l'eliminazione utente" });
      return { success: false, error: error.message };
    }
  };

  const updateCourse = async (courseData) => {
    try {
      const response = await apiClient.put(`/api/courses/${courseData._id}`, courseData);
      dispatch({
        type: ACTIONS.UPDATE_COURSE,
        payload: { _id: courseData._id, ...response.data },
      });
      return { success: true, data: response.data };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Errore durante la modifica corso' });
      return { success: false, error: error.message };
    }
  };

  const deleteCourse = async (courseId) => {
    try {
      await apiClient.delete(`/api/courses/${courseId}`);
      dispatch({ type: ACTIONS.DELETE_COURSE, payload: courseId });
      return { success: true };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: "Errore durante l'eliminazione corso" });
      return { success: false, error: error.message };
    }
  };

  const addCourse = async (courseData) => {
    try {
      const response = await apiClient.post('/api/courses', courseData);
      dispatch({ type: ACTIONS.ADD_COURSE, payload: response.data });
      return { success: true, data: response.data };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.response.data.message + ': ' + error.response.data.error });
      return { success: false, error: error.message };
    }
  };

  const addEnrollment = async (enrollmentData) => {
    try {
      const response = await apiClient.post('/api/enrollments', enrollmentData);
      dispatch({ type: ACTIONS.ADD_ENROLLMENT, payload: response.data });
      return { success: true, data: response.data };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: "Errore durante l'iscrizione" });
      return { success: false, error: error.message + ' ' + error.response.data.message };
    }
  };

  const deletePayment = async (paymentId) => {
    try {
      await apiClient.delete(`/api/payments/${paymentId}`);
      dispatch({ type: ACTIONS.DELETE_PAYMENT, payload: paymentId });

      // Ricarica gli enrollments perché dipendono dai pagamenti effettuati
      const enrollmentsRes = await apiClient.get('/api/enrollments');
      dispatch({ type: ACTIONS.UPDATE_ENROLLMENTS, payload: enrollmentsRes.data });

      return { success: true };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: "Errore durante l'eliminazione pagamento" });
      return { success: false, error: error.message };
    }
  };

  const addPayment = async (paymentData) => {
    try {
      const response = await apiClient.post('/api/payments', paymentData);

      dispatch({ type: ACTIONS.ADD_PAYMENT, payload: response.data });

      // Ricarica gli enrollments perché dipendono dai pagamenti effettuati
      const enrollmentsRes = await apiClient.get('/api/enrollments');
      dispatch({ type: ACTIONS.UPDATE_ENROLLMENTS, payload: enrollmentsRes.data });

      return { success: true, data: response.data };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Errore durante la creazione pagamento' });
      return { success: false, error: error.response.data.message };
    }
  };

  const addMember = async (memberData) => {
    try {
      const response = await apiClient.post('/api/memberships', memberData);
      dispatch({ type: ACTIONS.ADD_MEMBER, payload: response.data });
      return { success: true, data: response.data };
    } catch (error) {
      // console.log('xxxxxx data ', error.response.data);
      // console.log('xxxxxx', error.response.data.message + ' ' + error.response.data.error );
      dispatch({ type: ACTIONS.SET_ERROR, payload: error.response.data.message + ' ' + error.response.data.error });
      return { success: false, error: error.message };
    }
  };

  const updateMember = async (memberData) => {
    try {
      const response = await apiClient.patch(`/api/memberships/${memberData._id}`, memberData);
      dispatch({
        type: ACTIONS.UPDATE_MEMBER,
        payload: { _id: memberData._id, ...response.data },
      });
      return { success: true, data: response.data };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Errore durante la modifica membro' });
      return { success: false, error: error.message };
    }
  };

  const deleteMember = async (memberId) => {
    try {
      await apiClient.delete(`/api/memberships/${memberId}`);
      dispatch({ type: ACTIONS.DELETE_MEMBER, payload: memberId });
      return { success: true };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: "Errore durante l'eliminazione membro" });
      return { success: false, error: error.message };
    }
  };

  const addMembershipList = async (listData) => {
    try {
      const response = await apiClient.post('/api/membership-list', listData);
      dispatch({ type: ACTIONS.ADD_MEMBERSHIP_LIST, payload: response.data.data });
      return { success: true, data: response.data.data };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Errore durante la creazione lista membri' });
      return { success: false, error: error.message };
    }
  };

  const updateMembershipListItem = async (itemData) => {
    try {
      const response = await apiClient.put(`/api/membership-list/${itemData._id}`, itemData);
      dispatch({ type: ACTIONS.UPDATE_MEMBERSHIP_LIST_ITEM, payload: response.data.data });
      return { success: true, data: response.data.data };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Errore durante la modifica elemento lista membri' });
      return { success: false, error: error.message };
    }
  };

  const deleteMembershipList = async (listId) => {
    try {
      await apiClient.delete(`/api/membership-list/${listId}`);
      dispatch({ type: ACTIONS.DELETE_MEMBERSHIP_LIST, payload: listId });
      return { success: true };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: "Errore durante l'eliminazione lista membri" });
      return { success: false, error: error.message };
    }
  };

  const updateEnrollment = async (enrollmentId, updateData) => {
    try {
      const response = await apiClient.patch(`/api/enrollments/${enrollmentId}`, updateData);
      dispatch({
        type: ACTIONS.UPDATE_ENROLLMENT,
        payload: { _id: enrollmentId, ...response.data },
      });
      return { success: true, data: response.data };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Errore durante la modifica iscrizione' });
      return { success: false, error: error.message };
    }
  };

  const deleteEnrollment = async (enrollmentId) => {
    try {
      await apiClient.delete(`/api/enrollments/${enrollmentId}`);
      dispatch({ type: ACTIONS.DELETE_ENROLLMENT, payload: enrollmentId });
      return { success: true };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: "Errore durante l'eliminazione iscrizione" });
      return { success: false, error: error.message };
    }
  };

  const suspendEnrollment = async (enrollmentId) => {
    try {
      const response = await apiClient.post(`/api/enrollments/${enrollmentId}/suspend`);
      dispatch({
        type: ACTIONS.UPDATE_ENROLLMENT,
        payload: { _id: enrollmentId, ...response.data },
      });
      // Ricarica i corsi perché occupedSeats dipende dai enrollment sospesi
      // const coursesRes = await apiClient.get('/api/courses');
      // dispatch({ type: ACTIONS.UPDATE_COURSES, payload: coursesRes.data });
      // invece di fare la chiamata aggiorna direttamente lo stato locale
      // console.log(response.data);
      dispatch({ type: ACTIONS.UPDATE_COURSES, payload: state.courses.map((c) => {
        if (c._id === response.data.courseId) {
          console.log(response.data.course.occupiedSeats);
          return { ...c, occupiedSeats: response.data.course.occupiedSeats };
        }
        return c;
      }) });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.message || "Errore durante la sospensione dell'iscrizione";
      dispatch({ type: ACTIONS.SET_ERROR, payload: message });
      return { success: false, error: message };
    }
  };

  const unsuspendEnrollment = async (enrollmentId) => {
    try {
      const response = await apiClient.post(`/api/enrollments/${enrollmentId}/unsuspend`);
      dispatch({
        type: ACTIONS.UPDATE_ENROLLMENT,
        payload: { _id: enrollmentId, ...response.data },
      });
      dispatch({ type: ACTIONS.UPDATE_COURSES, payload: state.courses.map((c) => {
        if (c._id === response.data.courseId) {
          return { ...c, occupiedSeats: response.data.course.occupiedSeats };
        }
        return c;
      }) });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.message || "Errore durante l'attivazione dell'iscrizione";
      dispatch({ type: ACTIONS.SET_ERROR, payload: message });
      return { success: false, error: message };
    }
  };

  const refreshData = () => {
    fetchAllData();
  };

  const clearError = () => {
    dispatch({ type: ACTIONS.SET_ERROR, payload: null });
  };

  const value = {
    ...state,
    updateUser,
    deleteUser,
    updateCourse,
    deleteCourse,
    addCourse,
    addEnrollment,
    addPayment,
    deletePayment,
    addMember,
    updateMember,
    deleteMember,
    addMembershipList,
    updateMembershipListItem,
    deleteMembershipList,
    updateEnrollment,
    deleteEnrollment,
    suspendEnrollment,
    unsuspendEnrollment,
    refreshData,
    clearError,
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};

// Hook
export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};
